import {create, UseBoundStore} from 'zustand';
import {DEFAULT_DATE_FROM, defaults} from '../Constants';
import {AlertType, DEFAULT_CUSTOM_IMAGE_SCALE, EditorControlMode, ImageryOptions} from "../@types/enums";
import {ViewerState} from "../@types/store";
import {readDashboardLayoutVariant} from '../helpers/dashboardLayoutVariant';
import * as THREE from 'three';

const readSimpleTreeLook = (): boolean => {
    try {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem('isSimpleTreeLook') === 'true';
    } catch {
        return false;
    }
};

export const useViewerStore = create<ViewerState>()(() => ({

    //Scene Viewer Instance
    sceneViewer: null,

    //User Profile Photo (Avatar)
    isProfilePhotoAvailable: false,
    userProfilePhoto: undefined,

    //Sites Table Filter
    isApplyFilter: false,
    isDateFilterApplied: false,
    isStageFilterApplied: false,
    isAuroraOnlyFilter: false,
    stageSelectedFilter: [],
    dateSelectedFilter: {
        dateFrom: DEFAULT_DATE_FROM,
        dateTo: null,
    },

    //Ground Mount ( Flat Roof ) Properties
    selectedFlatRoofAzimuth: 0,
    selectedFlatRoofPitch: 0,
    selectedFlatRoofHeight: 0,
    selectedFlatRoofIsCarport: false,
    selectedFlatRoofLength: 0,
    selectedFlatRoofBreadth: 0,
    selectedFlatRoofPosX: 0,
    selectedFlatRoofPosY: 0,
    selectedFlatRoofScaleX: 0,
    selectedFlatRoofScaleY: 0,

    //Imagery
    imageryType: ImageryOptions.DEFAULT,
    mapCoordinates: { latitude: 0, longitude: 0 },

    //Custom Imagery
    customImagery: null,
    customImageFile: null,
    customImageScale: DEFAULT_CUSTOM_IMAGE_SCALE,
    customImageRotation: 0,

    //Pathname ( Routing Views )
    pathname: '/configure/roof',
    unitContext: undefined,

    pitchDisplayMode: 'Pitch Override',

    //Saved Design ( Finalized Site Design )
    designID: 0,
    isInActiveDesign: false,
    isNonFinalizedSavedDesign: false,
    nonFinalizedDesignData: null,
    selectedSiteDesign: null,
    siteDesigns: {
        aurora: null,
        eagleView: null,
    },
    designList: [],
    oldDesignList: [],
    isloadingOldDesignList: false,
    selectedDesignIndex: -1,
    isDesignLoading: false,
    efficiencyOverride: null,

    //Layers Visibility
    layerVisibilityState: {
        roof: true,
        obstruction: true,
        tree: true,
        setback: true,
        pathway: true,
        modules: true,
        irradiance: true,
        maps: true,
        terrain: true,
    },

    //Engineering
    engDownloadProgress: 0,

    //Site Details
    sitejson: '',
    siteAddress: '',

    //Roof , Wall and Obstruction Color
    roofSelectedColor: defaults.ROOF_FACE_COLOR,
    wallSelectedColor: defaults.WALL_COLOR,
    obstructionSelectedColor: defaults.OBSTRUCTION_COLOR,

    //Event
    isTypeEvent: false,
    isCtrlKeyDown: false,

    //Roof Square
    isRoofSquareDataUnavailable: false,
    isRoofSquarePriceUpdating: false,
    isRoofSquareInitialDataLoaded: false,
    isRoofSquarePriceUpdated: false,
    roofSquareErrorMessage: 'Floor',
    selectedPricingTier: undefined,
    isRoofActiveStatusUpdating: false,
    roofSquareTableData: [],
    roofSquareRetries: 0,
    roofSquareDataFromPanda: [],
    activeStatuses: [],
    roofSquarePricingData: {},
    roofsAltered: [],
    isRoofLabelOverrideByUser: false,

    //Setback
    isSetbackOverride: false,

    //Roof Active/ De-active
    deactivatedRoofIdArr: [],

    //Losses
    selectedLossesVendor: null,
    pciProductionLossesVendor: null,
    pciProductionLossesPending: null,

    //Context Menu
    refreshContextMenu: false,

    //Roof Pitch Override
    roofPitchOverrideArr: [],
    isPitchOverrideByUser: false,

    //Loader
    initialLoadLoadingContextRef: undefined,
    loadingCompleted: false,
    siteLoadingError: null,
    siteMultipliersPendingNotice: null as string | null,
    /** When set, shows the 3D-unavailable modal (above dashboard chrome). */
    siteViewerUnavailablePrompt: null as string | null,
    isBgCalc: false,
    isSavingInProgress: false,
    refreshingShade: false,


    //2D- 3D View
    is2DModeActive: false,
    is3DModeActive: true,

    //Orbit / Pan
    editorControlMode: EditorControlMode.orbit,

    //Iframe ( oneButton )
    isIframeEmbeddedInOneButton: 'false',
    /** Canvas-only embed (pre-contract top view): hide editor chrome / dashboard UI. */
    isViewportOnlyEmbed: false,

    //Sun Path Simulation
    sunDate: 1,
    showSunPathLine: false,
    isSunSimulationRunning: false,

    //Tape Measure
    isTapeMeasureActive: false,
    snappingDistanceForTapeMeasure: 1,

    //Sidebar ( UI )
    isLeftSidebarOpen: false,
    isRightSidebarOpen: true,
    mobileSidebarTabLabel: 'Sidebar',
    mobilePopoutTabLabel: 'Popout',
    isDashboardOpen: true,
    idleRedirectEnabled: false,
    /** Deferred ACCESS_MULTIPLIERS chain until user opens the 3D viewer (dashboard-first load). */
    siteViewerCalculationsPending: false,
    /** GLB/Aurora model draw deferred until user opens oneDRAW (dashboard-first). */
    deferredSiteModelLoad: null as {
        designData: unknown;
        sfid: string;
        modelType: string;
    } | null,
    siteModelLoadPending: false,
    siteModelLoadInProgress: false,
    dashboardLayoutVariant: readDashboardLayoutVariant(),
    designsMenubarSignal: 0,
    designsMenubarTab: 'ai',

    //Move Mode State
    isMoveRoofActive: false,
    isMoveTreeActive: false,
    isMoveObstructionActive: false,
    isMoveModuleActive: false,
    hasTreeMoved: false,


    //Add Mode State
    isAddTreeActive: false,
    isAddObstructionActive: false,
    isAddModuleActive: false,


    //Touch Events
    isTapandHold: false,
    isTabandHoldUp: false,


    //Alert or Modal
    viewerAlert: {
        show: false,
        title: '',
        message: '',
        messageType: AlertType.info,
        isModal: false,
        isToast: false,
        isCloseAfterDisplay: false,
    },
    isLightReachWarningDismissedByUser: false,
    lightReachDismissedViolationKey: '',
    lightReachViolationKey: '',


    //Units
    roofUnitOfMeasure: 'ft',
    treeUnitOfMeasure: 'ft',
    obstructionUnitOfMeasure: 'ft',

    //Wall Height ( Roof Height )
    wallInitialHeight: 0,
    wallEditedHeight: 0,

    //Tree Cart
    treeArr: [],
    treeCartItems: [],
    treeCartTotalCost: 0,
    updateTreeCartBtn: false,

    //EPC
    epcPriceData: [],
    ppwValue: 0,
    freezePPW: false,
    initialPPWReady: false,
    solarRateSettings: undefined,
    epcValue: 0,
    pricingType: 'PPW',
    pricingValue: '',
    isEscalatorSelected: false,
    escalatorValue: undefined,
    annualUsage: null,
    epcEstimatedProduction: null,
    customerSiteRestrictions: '',
    epcMessage: '',
    epcCalculatedPrice: '- Quote Not Run -',
    totalSunnovaEPCWithTrees: '- Quote Not Run -',


    //Setback Notes
    setbackNotes: [],
    groundMountNotes: [],
    setbackInfoObject: {
        ridge: 0,
        valley: 0,
        eave: 0,
        rake: 0,
        other: 0
    },
    aHJ_Township: '',


    //Roof Tag
    isRoofTagVisible: false,
    isRoofIDTriangleVisible: false,

    //Labels
    isTreeMultiplierLabelVisible: false,
    ghostOnlyGreenImpactTrees: false,
    treeGreenImpactEligibility: {} as Record<string, boolean>,
    treeGreenImpactEligibilityVersion: 0,
    isModuleMultiplierLabelVisible: false,
    isTreeLabelVisible: true,
    modulesMultipliersEnabled: false,
    isTreeHeightLabelVisible: false,

    //Box Select
    isBoxSelectMode: false,

    //Push Pull Tool
    isPushPullMode: false,

    //Mouse Events
    pointerDownSelectedObjectType: null,
    pointerDownSelectedObject: new THREE.Object3D(),
    isPointerOnObject: false,
    selRoofFaceArr: [],

    //Module Type List
    moduleList: [],
    activeModule: {
        key: 116,
        text: "Hanwha - Q.PEAK DUO BLK ML-G10 plus 410",
        value: "Hanwha - Q.PEAK DUO BLK ML-G10 plus 410",
        power: 410,
        w: 1.045,
        h: 1.879,
        manufacturer: "Hanwha"
    },

    //Utils
    isUserRedirectedToSiteAfterLogin: false,
    isSiteReCentered: false,
    isExpandPopup: false,
    isModalOpenInInterface: false,
    refreshQuestionableRoof: false,
    modulePlacementStatus: '',
    isRefreshDone: false,

    //Flags

    //Street View Mode Flag
    isStreetViewVisible: false,

    //Tree Flags
    isHideAllGhostTrees: false,
    isHideAllSelectedTrees: false,
    isTreePerimeterToolActive: false,
    isSimpleTreeLook: readSimpleTreeLook(),

    //What If Analysis
    isHideAllTreeWhatIfAnalysis: false,
    isHideGhostTreeWhatIfAnalysis: false,
    isHideAllObstWhatIfAnalysis: false,
    isHideGhostObstWhatIfAnalysis: false,

    //Obstruction Flags
    isHideAllGhostObstructions: true,
    isGhostObstructionUpdated: false,

    //Roof/Building Flags ( Engineering )
    isRoofChange: false,
    isBuildingChange: false,

    //Engineering Flags
    isEngineeringFieldsChange: false,
    isResetEdata: false,

    //----------Production Calculation----------

    //Shade Point Ray Testing
    rayIntersectArr: [],
    isLargeDesignShadeGatingActive: false,
    shadeEnabledRoofFaceIds: [],

    //Module Texture
    moduleSolarAccess: true, // Module Color based on Solar Access flag

    //Roof Area
    roofArea: 0,
    roofAreaSize: 0,

    //Circular Chart
    modeCircleSolar: 0,

    //Irradiance Calculation
    roofIrradiance: 0,
    irradianceMonth: [],

    //-----Total System Efficiency -----

    //Total System Size
    totalSystemSize: 0,

    //Utility Production
    utilityWattage: 0,

    //Actual Production
    actualWattage: 0,

    //Efficiency
    efficiency: 0,

    //Multiplier
    multiplier: 0,

    //Total Module Count
    modeCount: 0,

    //Monthly Production Estimate
    individualRoofSolarAccessMonthly: [],

    //Module Multiplier ( EPC )
    moduleMultiplierAverage: 0,

    //Weighted Solar Average
    weightedSolarAverage: '',

    //Roof Wise Production Calculation

    //Roof Information Table
    roofInfoArr: [],

    //Irradiance Chart
    modeChartSolar: Array(12).fill(0),
    modeChartIrr: Array(12).fill(0),

    //33 Percent Module Rule ( 33% Percent Modules Per Building Based on Roof Area )
    buildingWise33PercentRuleModuleCountList: [],


    //Autofill Module Count ( Context Menu )
    clickedRoofAutofillModuleCountPortrait: 0,
    clickedRoofAutofillModuleCountLandscape: 0,
    clickedRoofAutofillModuleCountBoth: 0,

    //Recalculation of Shade on Finalization ( Roof Index and Length )
    shadeCalculatingIdx: 0,
    shadeCalculatingLength: 0,

    //Annual Usage from Salesforce
    SFannualUsage: null,

    //is Drawing/Measuring Active Flag
    manualZone: false,

    //Accessory Building
    accessoryBuildingList: [],

    //Master Data
    masterDataList: null,

    //Building Data
    siteDesignDetails: [],
    rsaNotesFromDesign: '',
    rsaStatusFromDesign: undefined,
    siteBuildingData: [],
    buildingListData: [],

    //Saved Production/Design Info
    savedAnnualIdealMultiplier: 0,
    savedRoofModulesLayout: [],
    defaultLayoutModulesAtInitialLoad: [],
    savedLocationCoordinates: {
        latitude: 0,
        longitude: 0,
    },
    savedRoofFaceIdArray: [],
    savedRoofEdgeTypeCount: {
        ridge: 0,
        valley: 0,
        eave: 0,
        rake: 0,
        other: 0,
    },
    savedRoofWiseIdealMultiplier: [],
    savedRoofWiseAccessMultiplierDataArray: [],
    savedAccessMultipliersForSolarRoofs: [],

    //Selected Trees and Obstructions
    selectedTrees: [],
    selectedObstruction: undefined,
    obstUpdated : false,
    treeCuttingAiOptionsRequestId: 0,
    treeCuttingScenariosModalOpen: false,

    //UI Elements Ref
    refs: {},
    productType : "",
    //Ballasted Rack
    isBallastedRackTypeModuleActive: false,
    isBallastedRackingMeeting25percentRule : false,
    isBallastedRackUpdated : false,
    doesDesignContainFlatRoof : false,
    activeBallastRackDirection : 'parallel',
    ballastedRackModuleAngle : 10,
    customAzimuth: 0,

    isUserHaveCapabilityToAddPanelsPastSetbacks : false,
    detachedStructureBuildingIds : [],
    
    isBallastedAngleLabelVisible : false,
    // AI module filling (RUN AI)
    aiModuleFillingScenarios: [],
    aiModuleFillingLoading: false,
    aiModuleFillingError: null,
    aiModuleFillingTreeScenariosByLayoutKey: {},
    aiModuleFillingDesignId: null,
    aiModuleFillingLayoutVariants: [],
    aiCategoryLayoutVariants: {},
    aiResolvedRoofCombinations: [] as Array<{ name: string; roofFaceIds: string[] }>,
    isAiGeneratedDesign: false,

    // Run AI / viewer loading overlays
    aiLoading: false,
    aiLoadingStep: '',
    aiRunStartMs: null as number | null,
    aiRunElapsedMs: null as number | null,

    // Thumbnails / snapshots
    aiLayoutVariantSnapshots: {},
    layoutSnapshotsByKey: {},
    aiDesignSnapshots: {},
    loadingSavedResult: false,

    // Packing Slip
    financePartnerDetails: { name: '', id: 0, productType: 'S'},
    useDesignCenteredSunPosition: true,
}));

